import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { useAuth } from '../../shared/auth';
import { useDebouncedValue } from '../../shared/useDebouncedValue';
import { Icon } from '../../ui/Icon';
import { Modal } from '../../ui/Modal';

interface HousingLocation {
  id: string;
  code: string;
  displayName: string;
  address: string;
  doorCode: string;
  wifiName: string;
  wifiPassword: string;
  houseManagerName: string;
  houseManagerPhone: string;
  totalBeds: number;
  occupiedBeds: number;
  availableBeds: number;
  currentOccupancy: number; // count of LINKED clients (≤ occupiedBeds)
  active: boolean;
}

interface SyncRun {
  id: string;
  startedAt: string;
  completedAt: string | null;
  triggeredBy: string;
  status: string;
  tabsScanned: number;
  rowsSeen: number;
  placementsOpened: number;
  placementsClosed: number;
  placementsUpdated: number;
  pendingLinksCreated: number;
  programMismatches: number;
  writebacksAttempted: number;
  writebacksSucceeded: number;
  errorMessage: string | null;
  // Per-tab outcome map: tab label → counts + optional error string. Drives
  // the sync-run detail modal (esp. for PARTIAL runs, where some tabs failed).
  tabBreakdown?: Record<string, TabBreakdownEntry> | null;
}

interface TabBreakdownEntry {
  rows?: number;
  opened?: number;
  updated?: number;
  closed?: number;
  pending?: number;
  mismatches?: number;
  error?: string | null;
}

interface PendingLink {
  id: string;
  housingLocationId: string;
  locationCode: string;
  rowKey: string;
  rawName: string;
  rawPhone: string;
  rawRoom: string;
  rawProgram: string;
  rawMoveInDate: string;
  candidateClientIds: string[];
  status: string;
  linkedPatientId?: string | null;
  linkedPatientName?: string | null;
  clinicLocationId?: string | null;
  clinicLocationCity?: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

/**
 * Admin Housing page. Three panels:
 *   1. Locations roster — one card per HousingLocation with current
 *      occupancy + house metadata.
 *   2. Recent sync runs — last 10 with status + counts.
 *   3. Pending links queue — unmatched bedboard rows awaiting operator action.
 *
 * "Sync now" button at the top fires an immediate sync (mirrors the
 * nightly cron logic). Outcome refreshes all three panels.
 */
export function HousingPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const qc = useQueryClient();
  // Sync run whose per-tab detail modal is open (null = closed).
  const [selectedRun, setSelectedRun] = useState<SyncRun | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const { data: locations } = useQuery<HousingLocation[]>({
    queryKey: ['housing-locations'],
    queryFn: () => api.get('/housing/locations').then((r) => r.data),
  });
  const { data: runs } = useQuery<SyncRun[]>({
    queryKey: ['housing-runs'],
    queryFn: () => api.get('/housing/runs?limit=10').then((r) => r.data),
    refetchInterval: 30_000, // poll while sync is happening
  });
  // Note: pending-links data is fetched INSIDE ResidentsPanel — both the
  // unified residents view and the "Link to client…" resolver share that
  // ['housing-residents-pending'] cache key.

  const sync = useMutation({
    mutationFn: () => api.post('/housing/sync-now').then((r) => r.data),
    onSuccess: (r: any) => {
      toast.success(
        `${r.status} — ${r.tabsScanned} tabs, ${r.rowsSeen} rows, ` +
          `+${r.placementsOpened} ${r.placementsUpdated > 0 ? `· ~${r.placementsUpdated}` : ''} ` +
          `${r.placementsClosed > 0 ? `· −${r.placementsClosed}` : ''}`,
      );
      qc.invalidateQueries({ queryKey: ['housing-locations'] });
      qc.invalidateQueries({ queryKey: ['housing-runs'] });
      qc.invalidateQueries({ queryKey: ['housing-pending-links'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Sync failed'),
  });

  // Google Sheets sync — separate data source, same business logic.
  const syncGoogle = useMutation({
    mutationFn: () => api.post('/housing/google/sync').then((r) => r.data),
    onSuccess: (r: any) => {
      toast.success(
        `Google ${r.status} — ${r.tabsScanned} tabs, ${r.rowsSeen} rows, ` +
          `+${r.placementsOpened} ${r.placementsUpdated > 0 ? `· ~${r.placementsUpdated}` : ''} ` +
          `${r.placementsClosed > 0 ? `· −${r.placementsClosed}` : ''}` +
          `${r.emrAutoLinked > 0 ? ` · ${r.emrAutoLinked} EMR-linked` : ''}`,
      );
      window.location.reload();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Google sync failed'),
  });

  // Destructive full reset — wipes all housing data. Guarded by a confirm().
  const clearData = useMutation({
    mutationFn: () => api.post('/housing/clear').then((r) => r.data),
    onSuccess: (r: any) => {
      toast.success(
        `Housing data cleared — ${r.pendingLinksDeleted} pending, ${r.placementsDeleted} placements, ` +
          `${r.locationsDeleted} locations, ${r.syncRunsDeleted} runs`,
      );
      qc.invalidateQueries({ queryKey: ['housing-locations'] });
      qc.invalidateQueries({ queryKey: ['housing-runs'] });
      qc.invalidateQueries({ queryKey: ['housing-pending-links'] });
      qc.invalidateQueries({ queryKey: ['housing-residents-placements'] });
      qc.invalidateQueries({ queryKey: ['housing-residents-pending'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Clear failed'),
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Housing</h1>
          <p className="text-sm text-ink-subtle">
            Sober-living bedboard data pulled nightly from the partner workbook. Configure under
            Settings → Housing Sync.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button className="btn-primary" disabled={sync.isPending} onClick={() => sync.mutate()}>
              {sync.isPending ? 'Syncing…' : 'Sync now'}
            </button>
            <button
              className="btn-secondary"
              disabled={syncGoogle.isPending}
              onClick={() => syncGoogle.mutate()}
            >
              {syncGoogle.isPending ? 'Syncing Google…' : 'Sync Google'}
            </button>
            <button
              className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
              disabled={clearData.isPending}
              onClick={() => setShowClearConfirm(true)}
              title="Permanently delete all housing data"
            >
              {clearData.isPending ? 'Clearing…' : 'Clear data'}
            </button>
          </div>
        )}
      </header>

      {/* Locations */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-ink">
          Locations{' '}
          <span className="text-2xs font-normal text-ink-subtle">
            ({locations?.length ?? 0})
          </span>
        </h2>
        {!locations || locations.length === 0 ? (
          <div className="card p-4 text-sm text-ink-subtle">
            No locations configured yet. Once you successfully Test Connection in Settings, the
            first sync will auto-create one location per tab.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {locations.map((l) => (
              <article key={l.id} className="card p-3">
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-ink">{l.displayName}</h3>
                  <span className="text-2xs text-ink-subtle">{l.code}</span>
                </div>
                <p className="text-xs text-ink-subtle">{l.address || '— no address —'}</p>

                {/* Occupancy bar: fills with occupied beds against total capacity */}
                {l.totalBeds > 0 && (
                  <div className="mt-2">
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-muted">
                      <div
                        className="h-full bg-brand-500"
                        style={{
                          width: `${Math.min(100, Math.round((l.occupiedBeds / l.totalBeds) * 100))}%`,
                        }}
                      />
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs">
                      <span className="font-medium text-ink">
                        {l.occupiedBeds}/{l.totalBeds} beds occupied
                      </span>
                      <span
                        className={
                          l.availableBeds > 0
                            ? 'pill-success'
                            : 'pill-muted'
                        }
                      >
                        {l.availableBeds} available
                      </span>
                      {l.currentOccupancy < l.occupiedBeds && (
                        <span
                          className="pill-warn"
                          title="Residents on the bedboard who aren't linked to a clinic client yet (check Pending links queue below)"
                        >
                          {l.occupiedBeds - l.currentOccupancy} unlinked
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {l.totalBeds === 0 && (
                  <div className="mt-2 text-2xs text-ink-subtle">
                    No bed rows detected yet — run a sync to populate.
                  </div>
                )}

                <div className="mt-2 flex flex-wrap items-center gap-x-3 text-2xs text-ink-subtle">
                  {l.doorCode && <span>Door: {l.doorCode}</span>}
                  {l.wifiName && (
                    <span>
                      WiFi: {l.wifiName}
                      {l.wifiPassword && ` · pwd ${l.wifiPassword}`}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Residents — unified searchable list of everyone on the bedboards
          (linked clients + pending unlinked rows). Link/Ignore actions are
          inline on pending rows; the Link-status filter controls visibility. */}
      <ResidentsPanel
        locations={locations ?? []}
        isAdmin={isAdmin}
        onChanged={() => qc.invalidateQueries()}
      />

      {/* Recent sync runs */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-ink">Recent sync runs</h2>
        {!runs || runs.length === 0 ? (
          <div className="card p-4 text-sm text-ink-subtle">
            No syncs yet. Click "Sync now" or wait for the nightly cron.
          </div>
        ) : (
          <div className="card overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-surface-subtle text-2xs uppercase tracking-wide text-ink-subtle">
                <tr>
                  <th className="px-3 py-2 text-left">Started</th>
                  <th className="px-3 py-2 text-left">Trigger</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Tabs</th>
                  <th className="px-3 py-2 text-right">Rows</th>
                  <th className="px-3 py-2 text-right">+Open</th>
                  <th className="px-3 py-2 text-right">~Upd</th>
                  <th className="px-3 py-2 text-right">−Close</th>
                  <th className="px-3 py-2 text-right">Pend</th>
                  <th className="px-3 py-2 text-right">Mismatch</th>
                  <th className="px-3 py-2 text-right">Writeback</th>
                  <th className="px-3 py-2 text-right">Details</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr
                    key={r.id}
                    className="cursor-pointer border-t border-line-subtle hover:bg-surface-subtle/40"
                    onClick={() => setSelectedRun(r)}
                    title="View per-tab sync details"
                  >
                    <td className="px-3 py-2 text-xs">
                      {new Date(r.startedAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.triggeredBy}</td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          r.status === 'OK'
                            ? 'pill-success'
                            : r.status === 'FAILED'
                              ? 'pill-danger'
                              : r.status === 'PARTIAL'
                                ? 'pill-warn'
                                : 'pill-muted'
                        }
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.tabsScanned}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.rowsSeen}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                      {r.placementsOpened}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-muted">
                      {r.placementsUpdated}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-700">
                      {r.placementsClosed}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.pendingLinksCreated}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.programMismatches}
                    </td>
                    <td className="px-3 py-2 text-right text-2xs">
                      {r.writebacksAttempted > 0
                        ? `${r.writebacksSucceeded}/${r.writebacksAttempted}`
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-2xs text-brand-700">View →</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedRun && (
        <SyncRunDetailModal run={selectedRun} onClose={() => setSelectedRun(null)} />
      )}

      {syncGoogle.isPending && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-white/80 backdrop-blur-sm">
          <svg
            className="h-10 w-10 animate-spin text-brand-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p className="text-sm font-medium text-ink-subtle">Syncing Google Sheets…</p>
        </div>
      )}

      <Modal
        open={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        title="Delete all housing data?"
        size="sm"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setShowClearConfirm(false)}>Cancel</button>
            <button
              className="rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
              disabled={clearData.isPending}
              onClick={() => {
                setShowClearConfirm(false);
                clearData.mutate();
              }}
            >
              Delete all data
            </button>
          </>
        }
      >
        <p className="text-sm text-ink">
          This permanently removes every location, resident/pending row, placement, and sync run,
          and resets clients' housing status.
        </p>
        <p className="mt-2 text-sm font-medium text-rose-600">This cannot be undone. A sync will repopulate it.</p>
      </Modal>
    </div>
  );
}

/**
 * Detail modal for a single housing sync run. Shows the run summary plus a
 * per-tab breakdown table — the place to understand WHY a run came back
 * PARTIAL (which tabs returned 0 rows / errored) or FAILED.
 */
function SyncRunDetailModal({ run, onClose }: { run: SyncRun; onClose: () => void }) {
  const tabs = Object.entries(run.tabBreakdown ?? {});
  const erroredTabs = tabs.filter(([, t]) => !!t.error);
  const statusPill =
    run.status === 'OK'
      ? 'pill-success'
      : run.status === 'FAILED'
        ? 'pill-danger'
        : run.status === 'PARTIAL'
          ? 'pill-warn'
          : 'pill-muted';

  return (
    <Modal open={true} onClose={onClose} title="Sync run details" size="lg">
      {/* Summary */}
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-subtle">
        <span className={statusPill}>{run.status}</span>
        <span>
          <strong className="text-ink">Started:</strong> {new Date(run.startedAt).toLocaleString()}
        </span>
        {run.completedAt && (
          <span>
            <strong className="text-ink">Finished:</strong>{' '}
            {new Date(run.completedAt).toLocaleString()}
          </span>
        )}
        <span>
          <strong className="text-ink">Trigger:</strong> {run.triggeredBy}
        </span>
      </div>

      <div className="mb-3 grid grid-cols-3 gap-2 text-center text-2xs sm:grid-cols-7">
        <Stat label="Tabs" value={run.tabsScanned} />
        <Stat label="Rows" value={run.rowsSeen} />
        <Stat label="+Open" value={run.placementsOpened} tone="emerald" />
        <Stat label="~Upd" value={run.placementsUpdated} />
        <Stat label="−Close" value={run.placementsClosed} tone="rose" />
        <Stat label="Pending" value={run.pendingLinksCreated} />
        <Stat label="Mismatch" value={run.programMismatches} />
      </div>

      {run.errorMessage && (
        <div className="mb-3 rounded border border-rose-300 bg-rose-50 p-2 text-xs text-rose-700">
          <strong>Run error:</strong> {run.errorMessage}
        </div>
      )}

      {(run.status === 'PARTIAL' || erroredTabs.length > 0) && (
        <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-2xs text-amber-800">
          {erroredTabs.length} of {tabs.length} tab{tabs.length === 1 ? '' : 's'} could not be
          imported (0 rows or an error). The remaining tabs synced normally, and placements on the
          skipped tabs were left untouched.
        </p>
      )}

      <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
        Per-tab breakdown
      </h3>
      {tabs.length === 0 ? (
        <p className="rounded border border-line-subtle p-3 text-sm text-ink-subtle">
          No per-tab detail was recorded for this run.
        </p>
      ) : (
        <div className="max-h-80 overflow-y-auto rounded border border-line-subtle">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-surface-subtle text-2xs uppercase tracking-wide text-ink-subtle">
              <tr>
                <th className="px-2 py-1.5 text-left">Tab</th>
                <th className="px-2 py-1.5 text-right">Rows</th>
                <th className="px-2 py-1.5 text-right">+Open</th>
                <th className="px-2 py-1.5 text-right">~Upd</th>
                <th className="px-2 py-1.5 text-right">Pend</th>
                <th className="px-2 py-1.5 text-left">Result</th>
              </tr>
            </thead>
            <tbody>
              {tabs.map(([name, t]) => (
                <tr
                  key={name}
                  className={`border-t border-line-subtle/60 ${t.error ? 'bg-amber-50/60' : ''}`}
                >
                  <td className="px-2 py-1.5 font-medium text-ink">{name}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{t.rows ?? 0}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700">
                    {t.opened ?? 0}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-ink-muted">
                    {t.updated ?? 0}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{t.pending ?? 0}</td>
                  <td className="px-2 py-1.5">
                    {t.error ? (
                      <span className="text-amber-800" title={t.error}>
                        ⚠ Skipped — {t.error}
                      </span>
                    ) : (
                      <span className="text-emerald-700">OK</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'emerald' | 'rose';
}) {
  const toneCls = tone === 'emerald' ? 'text-emerald-700' : tone === 'rose' ? 'text-rose-700' : 'text-ink';
  return (
    <div className="rounded border border-line-subtle bg-surface-subtle/40 px-2 py-1.5">
      <div className={`text-sm font-semibold tabular-nums ${toneCls}`}>{value}</div>
      <div className="text-ink-subtle">{label}</div>
    </div>
  );
}

interface PlacementRow {
  id: string;
  clientId: string;
  housingLocationId: string;
  locationCode: string;
  locationDisplayName: string;
  clientName: string;
  cohortId: string;
  clinicLocationCity: string | null;
  roomNumber: string;
  contactPhone: string;
  sourceProgramText: string;
  startDate: string;
  isCurrent: boolean;
}

interface UnifiedResidentRow {
  key: string;
  // null when the resident hasn't been linked to a clinic client yet
  clientId: string | null;
  pendingLinkId: string | null;
  housingLocationId: string;
  locationCode: string;
  locationDisplayName: string;
  // For linked: "Alice Alpha". For pending: raw name from bedboard.
  name: string;
  cohortId: string | null;
  clinicLocationCity: string | null;
  clinicLocationId: string | null;
  roomNumber: string;
  phone: string;
  programText: string;
  moveInDate: string;
  linkedToClinic: boolean;
  // For pending rows only: which sub-status. 'PENDING' = needs operator action.
  // 'PROMOTED' = an IntakeCandidate has been created from this row; awaiting
  // a Client to be created via the intake wizard. 'IGNORED' rows are filtered
  // out of the residents list entirely (only kept for audit).
  pendingStatus?: 'PENDING' | 'PROMOTED';
  // Soft cross-reference to an EMR patient (pending rows only). Set via the
  // "Link EMR" action; does not affect clinic linkage.
  linkedPatientId?: string | null;
  linkedPatientName?: string | null;
}

/**
 * Residents finder. Shows EVERYONE on the bedboards — both linked clients
 * (HousingPlacement) and pending bedboard rows we haven't linked yet
 * (HousingPendingLink). Filters work across both sets so the case manager
 * sees the full housing picture at any time, regardless of how much
 * link-confirmation work has been done.
 *
 * In-memory filter for snappy UX (~100 residents per clinic = trivial).
 *
 * Program dropdown is built from DISTINCT program text actually present
 * in the merged data, so it adapts to "AM IOP", "PM IOP", "PHP", etc.
 * without a hardcoded enum.
 */
function ResidentsPanel({
  locations,
  isAdmin,
  onChanged,
}: {
  locations: HousingLocation[];
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [programFilter, setProgramFilter] = useState<string>('');
  const [linkedFilter, setLinkedFilter] = useState<string>(''); // '' | 'linked' | 'pending'
  const [search, setSearch] = useState<string>('');

  const { data: clinicLocations = [] } = useQuery<{ id: string; city: string; name: string }[]>({
    queryKey: ['locations-active'],
    queryFn: () => api.get('/locations', { params: { activeOnly: 'true' } }).then((r) => r.data),
  });

  // Patch clinic location on a linked client.
  const patchClinicLocation = useMutation({
    mutationFn: ({ clientId, locationId }: { clientId: string; locationId: string | null }) =>
      api.patch(`/clients/${clientId}`, { locationId }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['housing-residents-placements'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Clinic location updated');
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Update failed'),
  });

  // Patch clinic location on a pending (unlinked) bedboard row.
  const patchPendingClinicLocation = useMutation({
    mutationFn: ({ pendingId, clinicLocationId }: { pendingId: string; clinicLocationId: string | null }) =>
      api.patch(`/housing/pending-links/${pendingId}/clinic-location`, { clinicLocationId }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['housing-residents-pending'] });
      toast.success('Clinic location saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Update failed'),
  });

  // Track which pending row the admin is linking (drives LinkClientModal).
  const [linkingPendingId, setLinkingPendingId] = useState<string | null>(null);
  // Track which pending row the admin is EMR-linking (drives LinkEmrPatientModal).
  const [emrLinkingPendingId, setEmrLinkingPendingId] = useState<string | null>(null);
  // Confirm modals for promote and ignore actions.
  const [promoteTarget, setPromoteTarget] = useState<{ id: string; name: string; clinicLocationId: string | null } | null>(null);
  const [ignoreTarget, setIgnoreTarget] = useState<{ id: string; name: string; locationCode: string } | null>(null);

  // Link/ignore/promote mutations for pending rows shown inline on this table.
  const ignore = useMutation({
    mutationFn: (id: string) =>
      api.post(`/housing/pending-links/${id}/ignore`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Marked ignored');
      onChanged();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Ignore failed'),
  });
  const promote = useMutation({
    mutationFn: (id: string) =>
      api.post(`/housing/pending-links/${id}/promote-to-pipeline`).then((r) => r.data),
    onSuccess: (r: any) => {
      const cohort = r?.suggestedCohort ? ` (suggested cohort ${r.suggestedCohort})` : '';
      toast.success(`Added to Pipeline${cohort}. Complete intake to auto-link.`);
      onChanged();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Promote failed'),
  });

  const { data: placements, isLoading: placementsLoading } = useQuery<PlacementRow[]>({
    queryKey: ['housing-residents-placements'],
    queryFn: () =>
      api.get('/housing/placements?currentOnly=true').then((r) => r.data),
  });
  const { data: pending, isLoading: pendingLoading } = useQuery<PendingLink[]>({
    queryKey: ['housing-residents-pending'],
    queryFn: () => api.get('/housing/pending-links').then((r) => r.data),
  });

  // Merge into one unified list. Linked entries first; pending after.
  const unified: UnifiedResidentRow[] = [
    ...(placements ?? []).map((p) => ({
      key: `p:${p.id}`,
      clientId: p.clientId,
      pendingLinkId: null,
      housingLocationId: p.housingLocationId,
      locationCode: p.locationCode,
      locationDisplayName: p.locationDisplayName,
      name: p.clientName,
      cohortId: p.cohortId,
      clinicLocationCity: p.clinicLocationCity ?? null,
      clinicLocationId: null,
      roomNumber: p.roomNumber,
      phone: p.contactPhone,
      programText: p.sourceProgramText,
      moveInDate: p.startDate,
      linkedToClinic: true,
    })),
    ...(pending ?? [])
      // Hide IGNORED rows from the residents list (they're audited but
      // operationally invisible). PROMOTED rows stay visible with a
      // distinct pill so admins know the next step.
      .filter((p) => p.status === 'PENDING' || p.status === 'PROMOTED')
      .map((p) => ({
        key: `q:${p.id}`,
        clientId: null,
        pendingLinkId: p.id,
        housingLocationId: p.housingLocationId,
        locationCode: p.locationCode,
        locationDisplayName: p.locationCode, // pending doesn't carry display name; fall back to code
        name: p.rawName,
        cohortId: null,
        clinicLocationCity: p.clinicLocationCity ?? null,
        clinicLocationId: p.clinicLocationId ?? null,
        roomNumber: p.rawRoom,
        phone: p.rawPhone,
        programText: p.rawProgram,
        moveInDate: p.rawMoveInDate,
        linkedToClinic: false,
        pendingStatus: p.status as 'PENDING' | 'PROMOTED',
        linkedPatientId: p.linkedPatientId ?? null,
        linkedPatientName: p.linkedPatientName ?? null,
      })),
  ];

  // Distinct program labels seen in the merged data → power the Program dropdown.
  const programs = Array.from(
    new Set(unified.map((r) => r.programText.trim()).filter((t) => t.length > 0)),
  ).sort();

  const filtered = unified.filter((r) => {
    if (locationFilter && r.housingLocationId !== locationFilter) return false;
    if (programFilter && r.programText.trim() !== programFilter) return false;
    if (linkedFilter === 'linked' && !r.linkedToClinic) return false;
    if (linkedFilter === 'pending' && r.linkedToClinic) return false;
    if (search) {
      const q = search.toLowerCase();
      const hit =
        r.name.toLowerCase().includes(q) ||
        (r.cohortId ?? '').toLowerCase().includes(q) ||
        r.roomNumber.toLowerCase().includes(q) ||
        r.phone.toLowerCase().includes(q);
      if (!hit) return false;
    }
    return true;
  });

  const isLoading = placementsLoading || pendingLoading;

  return (
    <section>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
        <Icon name="users" size={14} /> Residents
        <span className="text-2xs font-normal text-ink-subtle">
          ({filtered.length}
          {filtered.length !== unified.length ? ` of ${unified.length}` : ''} total
          {unified.length > 0 && (
            <>
              {' · '}
              <span className="text-emerald-700">
                {unified.filter((r) => r.linkedToClinic).length} linked
              </span>
              {' · '}
              <span className="text-amber-700">
                {unified.filter((r) => !r.linkedToClinic).length} pending
              </span>
            </>
          )}
          )
        </span>
      </h2>
      <div className="mb-2 flex flex-wrap items-end gap-2 rounded-md border border-line-subtle bg-surface-subtle/60 p-2.5">
        <label className="flex flex-col text-2xs text-ink-subtle">
          Location
          <select
            className="input h-8 w-48"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
          >
            <option value="">All locations</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.displayName} ({l.code})
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-2xs text-ink-subtle">
          Program
          <select
            className="input h-8 w-40"
            value={programFilter}
            onChange={(e) => setProgramFilter(e.target.value)}
          >
            <option value="">All programs</option>
            {programs.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-2xs text-ink-subtle">
          Link status
          <select
            className="input h-8 w-36"
            value={linkedFilter}
            onChange={(e) => setLinkedFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="linked">Linked only</option>
            <option value="pending">Pending only</option>
          </select>
        </label>
        <label className="flex flex-col text-2xs text-ink-subtle">
          Search
          <input
            className="input h-8 w-56"
            placeholder="name, cohort ID, room, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        {(locationFilter || programFilter || linkedFilter || search) && (
          <button
            className="btn-ghost h-8 text-xs"
            onClick={() => {
              setLocationFilter('');
              setProgramFilter('');
              setLinkedFilter('');
              setSearch('');
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="card p-3 text-sm text-ink-subtle">Loading residents…</div>
      ) : filtered.length === 0 ? (
        <div className="card p-3 text-sm text-ink-subtle">
          {unified.length > 0
            ? 'No residents match these filters.'
            : 'No residents yet. Click "Sync now" above to pull from the bedboard.'}
        </div>
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle text-2xs uppercase tracking-wide text-ink-subtle">
              <tr>
                <th className="px-3 py-2 text-left">Resident</th>
                <th className="px-3 py-2 text-left">Cohort</th>
                <th className="px-3 py-2 text-left">Housing location</th>
                <th className="px-3 py-2 text-left">Clinic location</th>
                <th className="px-3 py-2 text-left">Room</th>
                <th className="px-3 py-2 text-left">Program (sheet)</th>
                <th className="px-3 py-2 text-left">Phone</th>
                <th className="px-3 py-2 text-left">Move-in</th>
                <th className="px-3 py-2 text-left">Status</th>
                {isAdmin && <th className="px-3 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.key}
                  className="border-t border-line-subtle hover:bg-surface-subtle/40"
                >
                  <td className="px-3 py-2">
                    {r.clientId ? (
                      <Link
                        to={`/clients/${r.clientId}`}
                        className="font-medium text-brand-700 hover:underline"
                      >
                        {r.name}
                      </Link>
                    ) : (
                      <span className="font-medium text-ink">{r.name}</span>
                    )}
                    {!r.linkedToClinic && r.linkedPatientId && (
                      <div
                        className="mt-0.5 text-2xs text-emerald-700"
                        title="Cross-referenced to an EMR patient (no clinic client yet)"
                      >
                        EMR: {r.linkedPatientName || 'patient'}{' '}
                        <span className="font-mono">#{r.linkedPatientId}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.cohortId ? (
                      <span className="font-mono text-brand-700">{r.cohortId}</span>
                    ) : (
                      <span className="text-2xs text-ink-subtle">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.locationDisplayName !== r.locationCode ? (
                      <>
                        {r.locationDisplayName}{' '}
                        <span className="text-ink-subtle">({r.locationCode})</span>
                      </>
                    ) : (
                      r.locationCode
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.clientId ? (
                      <span className="text-ink">{r.clinicLocationCity || '—'}</span>
                    ) : r.pendingLinkId ? (
                      <select
                        className="input h-7 w-36 text-xs"
                        value={r.clinicLocationId ?? ''}
                        onChange={(e) =>
                          patchPendingClinicLocation.mutate({ pendingId: r.pendingLinkId!, clinicLocationId: e.target.value || null })
                        }
                      >
                        <option value="">— None —</option>
                        {clinicLocations.map((l) => (
                          <option key={l.id} value={l.id}>{l.city || l.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-ink-subtle">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.roomNumber || '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.programText || '—'}</td>
                  <td className="px-3 py-2 font-mono text-2xs">
                    {r.phone ? (
                      <a className="hover:underline" href={`tel:${r.phone}`}>
                        {r.phone}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-2xs">{r.moveInDate || '—'}</td>
                  <td className="px-3 py-2">
                    {r.linkedToClinic ? (
                      <span className="pill-success">Linked</span>
                    ) : r.pendingStatus === 'PROMOTED' ? (
                      <span className="pill-info" title="An IntakeCandidate has been created. Complete the intake in /pipeline to auto-link.">
                        In Pipeline
                      </span>
                    ) : (
                      <span className="pill-warn">Pending link</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2 text-right">
                      {!r.linkedToClinic && r.pendingLinkId && (
                        <div className="inline-flex gap-1">
                          {/* Link button shown for BOTH pending and promoted rows.
                              For promoted rows it's how the operator manually
                              picks which Client (if multiple matches exist OR
                              if the intake created the Client without auto-linking). */}
                          <button
                            className="btn-ghost h-7 w-7 p-0 inline-flex items-center justify-center"
                            onClick={() => setLinkingPendingId(r.pendingLinkId)}
                            title="Link… — Link this bedboard row to an existing clinic client"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                          </button>
                          <button
                            className="btn-ghost h-7 px-2 p-0 inline-flex items-center justify-center gap-1 relative whitespace-nowrap"
                            onClick={() => setEmrLinkingPendingId(r.pendingLinkId)}
                            title={r.linkedPatientId ? 'EMR linked ✓ — Click to change EMR link' : 'Link EMR — Cross-reference this bedboard row to an EMR patient'}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            <span className="text-xs">{r.linkedPatientId ? 'EMR ✓' : 'EMR'}</span>
                          </button>
                          {r.pendingStatus === 'PENDING' && (
                            <button
                              className="btn-ghost h-7 w-7 p-0 inline-flex items-center justify-center text-brand-700"
                              onClick={() => setPromoteTarget({ id: r.pendingLinkId!, name: r.name, clinicLocationId: r.clinicLocationId })}
                              title="Pipeline — Push this row into /pipeline to begin intake"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                {/* snake/coil pipeline icon */}
                                <path d="M2 5 H19 Q22 5 22 8 Q22 11 19 11 H5 Q2 11 2 14 Q2 17 5 17 H19 Q22 17 22 20"/>
                              </svg>
                            </button>
                          )}
                          {r.pendingStatus === 'PROMOTED' && (
                            <Link
                              to="/pipeline"
                              className="btn-ghost h-7 w-7 p-0 inline-flex items-center justify-center text-brand-700"
                              title="Pipeline → View in intake pipeline"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                {/* snake/coil pipeline icon */}
                                <path d="M2 5 H19 Q22 5 22 8 Q22 11 19 11 H5 Q2 11 2 14 Q2 17 5 17 H19 Q22 17 22 20"/>
                              </svg>
                            </Link>
                          )}
                          <button
                            className="btn-ghost h-7 w-7 p-0 inline-flex items-center justify-center text-rose-700"
                            onClick={() => setIgnoreTarget({ id: r.pendingLinkId!, name: r.name, locationCode: r.locationCode })}
                            title="Ignore — Mark as not your client (won't appear in queue again)"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Link modal — opened from inline "Link to client…" action on any
          pending row. Reuses the same fuzzy-search picker as before. */}
      {linkingPendingId && (
        <PendingLinkResolver
          pendingId={linkingPendingId}
          onClose={() => setLinkingPendingId(null)}
          onLinked={() => {
            setLinkingPendingId(null);
            onChanged();
          }}
        />
      )}

      {/* EMR cross-reference modal — pick an EMR patient (closest name match
          surfaced first, plus free search) and stamp their patient ID onto
          this bedboard row. Separate from the clinic-client "Link…" above. */}
      {emrLinkingPendingId && (
        <EmrLinkResolver
          pendingId={emrLinkingPendingId}
          onClose={() => setEmrLinkingPendingId(null)}
          onLinked={() => {
            setEmrLinkingPendingId(null);
            onChanged();
          }}
        />
      )}

      <Modal
        open={!!promoteTarget}
        onClose={() => setPromoteTarget(null)}
        title="Promote to Pipeline"
        size="sm"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setPromoteTarget(null)}>Cancel</button>
            <button
              className="btn-primary"
              disabled={promote.isPending || !promoteTarget?.clinicLocationId}
              title={!promoteTarget?.clinicLocationId ? 'Select a clinic location before promoting' : undefined}
              onClick={() => {
                if (promoteTarget) promote.mutate(promoteTarget.id);
                setPromoteTarget(null);
              }}
            >
              Promote
            </button>
          </>
        }
      >
        <p className="text-sm text-ink">
          Promote <span className="font-semibold">"{promoteTarget?.name}"</span> to the intake Pipeline?
        </p>
        {!promoteTarget?.clinicLocationId && (
          <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            A clinic location must be selected before promoting. Use the dropdown in the Clinic location column.
          </p>
        )}
      </Modal>

      <Modal
        open={!!ignoreTarget}
        onClose={() => setIgnoreTarget(null)}
        title="Ignore Resident"
        size="sm"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setIgnoreTarget(null)}>Cancel</button>
            <button
              className="rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
              disabled={ignore.isPending}
              onClick={() => {
                if (ignoreTarget) ignore.mutate(ignoreTarget.id);
                setIgnoreTarget(null);
              }}
            >
              Ignore
            </button>
          </>
        }
      >
        <p className="text-sm text-ink">
          Ignore <span className="font-semibold">"{ignoreTarget?.name}"</span> from{' '}
          <span className="font-semibold">{ignoreTarget?.locationCode}</span>? This resident won't appear in the queue again.
        </p>
      </Modal>
    </section>
  );
}

/**
 * Fetches the specific pending-link row by id and opens the LinkClientModal.
 * We do this instead of passing the full PendingLink prop because the parent
 * only stores the id — keeps the parent stateless and the modal self-contained.
 */
function PendingLinkResolver({
  pendingId,
  onClose,
  onLinked,
}: {
  pendingId: string;
  onClose: () => void;
  onLinked: () => void;
}) {
  const { data: pending } = useQuery<PendingLink[]>({
    queryKey: ['housing-residents-pending'],
    queryFn: () => api.get('/housing/pending-links').then((r) => r.data),
  });
  const target = (pending ?? []).find((p) => p.id === pendingId);
  if (!target) return null;
  return (
    <LinkClientModal pending={target} onClose={onClose} onLinked={onLinked} />
  );
}

function LinkClientModal({
  pending,
  onClose,
  onLinked,
}: {
  pending: PendingLink;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [search, setSearch] = useState(pending.rawName);
  const { data: clients } = useQuery({
    queryKey: ['clients-for-link', search],
    queryFn: () => api.get('/clients?status=ACTIVE').then((r) => r.data),
  });
  // Cohort master library — used to preview the program label that will be
  // written into the bedboard's Program cell when this link commits.
  const { data: cohortList } = useQuery({
    queryKey: ['cohorts'],
    queryFn: () => api.get('/cohorts').then((r) => r.data),
  });
  const cohortNameByCode = new Map<string, string>(
    (cohortList ?? []).map((c: any) => [c.code, c.name]),
  );

  const filtered = (clients ?? []).filter((c: any) => {
    const q = search.toLowerCase();
    return (
      !q ||
      c.firstName.toLowerCase().includes(q) ||
      c.lastName.toLowerCase().includes(q) ||
      c.cohortId.toLowerCase().includes(q)
    );
  });
  const link = useMutation({
    mutationFn: (clientId: string) =>
      api
        .post(`/housing/pending-links/${pending.id}/link`, { clientId })
        .then((r) => r.data),
    onSuccess: (r: any) => {
      const wb = r?.writeback;
      if (wb?.programWritten) {
        toast.success(`Linked · pushed program "${wb.programValue}" to ${pending.locationCode}`);
      } else if (wb?.error) {
        toast.message(`Linked. Bedboard writeback skipped: ${wb.error}`);
      } else {
        toast.success('Linked');
      }
      onLinked();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Link failed'),
  });

  return (
    <Modal open={true} onClose={onClose} title={`Link "${pending.rawName}" to a client`} size="lg">
      <div className="mb-3 rounded border border-line-subtle bg-surface-subtle/60 p-2 text-xs">
        <div>
          From <strong>{pending.locationCode}</strong> · Room {pending.rawRoom} · Phone{' '}
          {pending.rawPhone || '—'} · Program <em>{pending.rawProgram || '—'}</em>
        </div>
      </div>
      <p className="mb-3 text-2xs text-ink-subtle">
        Picking a client will (1) open a housing placement at <strong>{pending.locationCode}</strong>,
        (2) push that client's cohort label into the bedboard's <strong>Program</strong> cell so the
        housing partner sees the clinic's assignment, and (3) stamp the Talbot ID column if enabled
        and blank.
      </p>
      <input
        className="input mb-3"
        placeholder="Search name or cohort ID…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="max-h-80 overflow-y-auto rounded border border-line-subtle">
        {filtered.slice(0, 40).map((c: any) => {
          // Resolve the cohort code (e.g., "C") from cohortId (e.g., "C4")
          // and look up its master-library name. This is the value that
          // will be PATCH'd into the bedboard's Program cell on link.
          const code = (c.cohortId || '').replace(/\d+$/, '');
          const programPreview = cohortNameByCode.get(code) ?? code;
          return (
            <button
              key={c.id}
              className="flex w-full items-center justify-between border-b border-line-subtle/60 px-3 py-2 text-left text-sm hover:bg-surface-subtle/60 disabled:opacity-50"
              disabled={link.isPending}
              onClick={() => link.mutate(c.id)}
            >
              <span>
                <span className="font-mono font-semibold text-brand-700">{c.cohortId}</span>{' '}
                {c.firstName} {c.lastName}
                {c.externalRef && (
                  <span className="ml-2 text-2xs text-ink-subtle">EMR {c.externalRef}</span>
                )}
              </span>
              <span className="flex items-center gap-2">
                <span className="rounded bg-brand-50 px-1.5 py-0.5 text-2xs text-brand-700 ring-1 ring-brand-200">
                  will write Program: <strong>{programPreview}</strong>
                </span>
                <span className="text-2xs text-brand-700">Link →</span>
              </span>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="p-3 text-sm text-ink-subtle">No clients match. Refine search.</p>
        )}
      </div>
    </Modal>
  );
}

interface EmrPatient {
  id: string | number;
  firstName?: string | null;
  lastName?: string | null;
  middleInitial?: string | null;
  dob?: string | null;
  age?: number | null;
  phone?: string | null;
  mobilePhone?: string | null;
  email?: string | null;
  city?: string | null;
  state?: string | null;
  gender?: string | null;
}

/** Strip nicknames-in-quotes, punctuation, and collapse whitespace. */
function normName(s: string | null | undefined): string {
  return (s ?? '')
    .toLowerCase()
    .replace(/["'][^"']*["']/g, ' ')
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function lev(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1]! + 1, prev[j]! + 1, prev[j - 1]! + cost);
    }
    prev = curr;
  }
  return prev[b.length]!;
}

/**
 * Heuristic name-similarity score between a bedboard raw name and an EMR
 * patient. Order-independent token overlap with extra weight on exact
 * last/first-name hits and a small fuzzy allowance on the last name. Higher
 * is better; 0 means no usable overlap.
 */
function scorePatient(rawName: string, p: EmrPatient): number {
  const rn = normName(rawName);
  const pn = normName(`${p.firstName ?? ''} ${p.lastName ?? ''}`);
  if (!rn || !pn) return 0;
  if (rn === pn) return 1000;
  const rt = rn.split(' ');
  const pt = new Set(pn.split(' '));
  const rLast = rt[rt.length - 1] ?? '';
  const rFirst = rt[0] ?? '';
  const pLast = normName(p.lastName);
  const pFirst = normName(p.firstName);
  let score = 0;
  for (const t of new Set(rt)) if (pt.has(t)) score += 60;
  if (rLast && pLast && rLast === pLast) score += 300;
  else if (rLast && pLast && lev(rLast, pLast) <= 2) score += 150;
  if (rFirst && pFirst && rFirst === pFirst) score += 200;
  else if (rFirst && pFirst && pFirst[0] === rFirst[0]) score += 20;
  return score;
}

/**
 * Loads the pending-link row by id (same cache as the residents list) and
 * opens the EMR patient picker for it.
 */
function EmrLinkResolver({
  pendingId,
  onClose,
  onLinked,
}: {
  pendingId: string;
  onClose: () => void;
  onLinked: () => void;
}) {
  const { data: pending } = useQuery<PendingLink[]>({
    queryKey: ['housing-residents-pending'],
    queryFn: () => api.get('/housing/pending-links').then((r) => r.data),
  });
  const target = (pending ?? []).find((p) => p.id === pendingId);
  if (!target) return null;
  return <LinkEmrPatientModal pending={target} onClose={onClose} onLinked={onLinked} />;
}

function LinkEmrPatientModal({
  pending,
  onClose,
  onLinked,
}: {
  pending: PendingLink;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [search, setSearch] = useState(pending.rawName);
  // Server-side search: the EMR has thousands of patients, so we never pull the
  // whole table. We send the (debounced) query to the API, which returns at
  // most ~50 matches. Pre-seeded with the bedboard name so the right patient
  // surfaces on open.
  const debounced = useDebouncedValue(search.trim(), 300);
  const { data: patients, isFetching } = useQuery<EmrPatient[]>({
    queryKey: ['emr-patient-search', debounced],
    queryFn: () =>
      api.get('/patients', { params: { q: debounced, limit: 50 } }).then((r) => r.data),
    placeholderData: (prev) => prev, // keep prior results visible while the next search loads
  });

  // Score the (small) server result set by name similarity to highlight the
  // single closest match and order the list. Cheap — operates on ≤50 rows.
  const ranked = useMemo(
    () =>
      (patients ?? [])
        .map((p) => ({ p, score: scorePatient(pending.rawName, p) }))
        .sort((a, b) => b.score - a.score),
    [patients, pending.rawName],
  );
  const closest = ranked[0] && ranked[0].score >= 200 ? ranked[0].p : null;
  const filtered = ranked;

  const [mismatchError, setMismatchError] = useState<string | null>(null);

  const link = useMutation({
    mutationFn: (vars: { patientId: string | null; patientName: string | null }) =>
      api.post(`/housing/pending-links/${pending.id}/link-emr`, vars).then((r) => r.data),
    onSuccess: (_d, vars) => {
      toast.success(vars.patientId ? 'EMR patient linked' : 'EMR link removed');
      onLinked();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'EMR link failed'),
  });

  /** Extract last 10 digits from any phone string, or '' if unparseable. */
  function normalizePhone(raw: string | null | undefined): string {
    const digits = (raw ?? '').replace(/\D/g, '');
    if (digits.length === 10) return digits;
    if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
    return '';
  }

  const linkPatient = (p: EmrPatient) => {
    setMismatchError(null);

    // Validate: name AND phone must both match before linking.
    const bedName = normName(pending.rawName);
    const emrName = normName(`${p.firstName ?? ''} ${p.lastName ?? ''}`);
    const nameMatches = bedName && emrName && scorePatient(pending.rawName, p) >= 200;

    const bedPhone = normalizePhone(pending.rawPhone);
    const emrPhone = normalizePhone(p.mobilePhone) || normalizePhone(p.phone);
    const phoneMatches = bedPhone && emrPhone && bedPhone === emrPhone;

    if (!nameMatches && !phoneMatches) {
      setMismatchError(
        `Name and phone do not match the bedboard row. Bedboard: "${pending.rawName}" · ${pending.rawPhone || '—'}. EMR: "${p.firstName ?? ''} ${p.lastName ?? ''}" · ${p.mobilePhone || p.phone || '—'}.`,
      );
      return;
    }
    if (!nameMatches) {
      setMismatchError(
        `Name does not match. Bedboard: "${pending.rawName}". EMR: "${p.firstName ?? ''} ${p.lastName ?? ''}". Verify this is the same person before linking.`,
      );
      return;
    }
    if (!phoneMatches) {
      setMismatchError(
        `Phone does not match. Bedboard: "${pending.rawPhone || '—'}". EMR: "${p.mobilePhone || p.phone || '—'}". Verify this is the same person before linking.`,
      );
      return;
    }

    link.mutate({
      patientId: String(p.id),
      patientName: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || null,
    });
  };

  function PatientRow({ p, highlight }: { p: EmrPatient; highlight?: boolean }) {
    const isCurrent = pending.linkedPatientId === String(p.id);
    return (
      <button
        className={`flex w-full items-center justify-between border-b border-line-subtle/60 px-3 py-2 text-left text-sm hover:bg-surface-subtle/60 disabled:opacity-50 ${
          highlight ? 'bg-emerald-50' : ''
        }`}
        disabled={link.isPending}
        onClick={() => linkPatient(p)}
      >
        <span>
          <span className="font-medium text-ink">
            {p.firstName} {p.lastName}
          </span>
          <span className="ml-2 font-mono text-2xs text-ink-subtle">#{p.id}</span>
          <span className="ml-2 text-2xs text-ink-subtle">
            {p.dob ? `DOB ${String(p.dob).slice(0, 10)}` : ''}
            {p.age != null ? ` · ${p.age}y` : ''}
            {p.mobilePhone || p.phone ? ` · ${p.mobilePhone || p.phone}` : ''}
            {p.city ? ` · ${p.city}${p.state ? `, ${p.state}` : ''}` : ''}
          </span>
        </span>
        <span className="text-2xs text-brand-700">{isCurrent ? 'Linked ✓' : 'Link →'}</span>
      </button>
    );
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Link "${pending.rawName}" to an EMR patient`}
      size="lg"
    >
      <div className="mb-3 rounded border border-line-subtle bg-surface-subtle/60 p-2 text-xs">
        From <strong>{pending.locationCode}</strong> · Room {pending.rawRoom || '—'} · Phone{' '}
        {pending.rawPhone || '—'} · Program <em>{pending.rawProgram || '—'}</em>
      </div>
      <p className="mb-3 text-2xs text-ink-subtle">
        Records the EMR patient ID on this bedboard row as a cross-reference. This does not create a
        clinic client or open a housing placement — use <strong>Link…</strong> for that.
      </p>

      {pending.linkedPatientId && (
        <div className="mb-3 flex items-center justify-between rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs">
          <span>
            Currently linked to{' '}
            <strong>{pending.linkedPatientName || 'EMR patient'}</strong>{' '}
            <span className="font-mono">#{pending.linkedPatientId}</span>
          </span>
          <button
            className="btn-ghost h-7 px-2 text-xs text-rose-700"
            disabled={link.isPending}
            onClick={() => link.mutate({ patientId: null, patientName: null })}
          >
            Unlink
          </button>
        </div>
      )}

      {mismatchError && (
        <div className="mb-3 flex items-start gap-2 rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          <span className="mt-0.5 shrink-0 text-rose-500">⚠</span>
          <span>{mismatchError}</span>
          <button
            className="ml-auto shrink-0 text-rose-400 hover:text-rose-700"
            onClick={() => setMismatchError(null)}
          >
            ✕
          </button>
        </div>
      )}

      {isFetching && !patients ? (
        <div className="p-3 text-sm text-ink-subtle">Searching EMR patients…</div>
      ) : (
        <>
          {!pending.linkedPatientId && closest && (
            <div className="mb-3">
              <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
                Closest match
              </div>
              <div className="overflow-hidden rounded border border-emerald-200">
                <PatientRow p={closest} highlight />
              </div>
            </div>
          )}

          {!pending.linkedPatientId && (
            <input
              className="input mb-2"
              placeholder="Search EMR patients by name, patient ID, phone, DOB…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <div className="max-h-72 overflow-y-auto rounded border border-line-subtle">
            {filtered.map(({ p }) => (
              <PatientRow key={String(p.id)} p={p} />
            ))}
            {!isFetching && filtered.length === 0 && (
              <p className="p-3 text-sm text-ink-subtle">No EMR patients match. Refine search.</p>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
